// @ts-check

import React from 'react';
import PropTypes from 'prop-types';

import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import { reduxForm } from 'redux-form';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import DatePicker from 'infrastructure/js/components/controls/DatePicker/datepicker';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper';
import * as dialogHelper from 'infrastructure/js/components/Dialog/dialogHelper';
import moment from 'moment';
import PermissionManager from 'infrastructure/js/utils/permissionManager';

require('./autoSchedulerDialog.scss');

const LOCAL_STORAGE_KEY = 'AutoSchedulerDialogDates';




/**
 * @param {string} localDateTimeString
 */
const createLocalTimeObject = (localDateTimeString) => {
  return moment(localDateTimeString, 'YYYY-MM-DDTHH:mm:ss');
};


class AutoSchedulerDialog extends React.PureComponent {

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.dialogLabels = createLabelHelper('mat.dialog.');
    this.labels = createLabelHelper('mat.scheduler.operations.dialog.autoScheduler.');


    let now = moment();

    this.fromInitialDate = moment(now);
    this.toInitialDate = moment(now).add(7, 'days');


    this.schedulingPeriodMonths = PermissionManager.getOrgPreferences().schedulingPeriodMonths;

    this.minDate = moment(now).subtract(
      this.schedulingPeriodMonths, 'months');

    this.maxDate = moment(now).add(
      this.schedulingPeriodMonths, 'months');


    this.state = {
      curFromDate: null,
      curToDate: this.toInitialDate,
      curFromMinTime: this.calculateMinTime(this.fromInitialDate),
      curToMinTime: this.calculateMinTime(this.toInitialDate),
      curMaxTime: moment(now).endOf('day'),

      dynamicMinDate: moment(now),
      dynamicMaxDate: this.maxDate,

    };

  }

  /**
   * @returns {{ from: string, to: string } | false}
   */
  loadStoredDates() {
    const savedString = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!savedString) {
      return false;
    }
    
    let dateStrings = JSON.parse(savedString);
    return dateStrings;
  }


  /**
   * @param {{from: moment.Moment, to: moment.Moment}} dates
   */
  storeDates({ from, to }) {
    const fromDate = from?.utc().format() ?? moment().utcOffset(0, true).utc().format();
    const toDate = to.utc().format();
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ from: fromDate, to: toDate }));
  }


  /**
   * @param {{from: string, to: string}} dates
   */
  adjustStoredDatesToCurrentTime({ from, to }) {

    // Parse "from" string into moment or use current time.
    const fromObj = from ? createLocalTimeObject(from) : moment();


    let minDate = this.minDate;

    // If From date has elapsed.
    if (fromObj.isBefore(minDate)) {

      const newFrom = minDate.clone();
      newFrom
        .hour(fromObj.hour())
        .minute(fromObj.minute());


      // If the new From date is still before the minimum date,
      // add 7 days to it.
      if (newFrom.isBefore(minDate)) {
        newFrom.add(7, 'days');
      }

      const newTo = moment(newFrom);
      newTo.add(7, 'days');

      return { from: newFrom, to: newTo };
    }


    // Stored dates are valid as is, so just parse them.
    return { from: moment.utc(from), to: moment.utc(to) };

  }


  componentDidMount() {

    // Initialize the dialog with stored dates or default values.
    this.initializeStoredDates();

  }


  initializeStoredDates = () => {

    const storedDates = this.loadStoredDates();
    
    // Default values if no stored dates are found.
    if (!storedDates) {
      this.props.initialize({
        fromHorizonDate: null,
        toHorizonDate: this.toInitialDate.utcOffset(0, true),
      });
      return;
    }

    
    // Adjust stored dates to current time and ensure they are in UTC.

    const { from, to } = this.adjustStoredDatesToCurrentTime(storedDates);
    if (from) {
      from.utcOffset(0, true);
    }

    to.utcOffset(0, true);

    this.props.initialize({
      fromHorizonDate: from,
      toHorizonDate: to,
    });

    this.setState({
      curFromDate: from,
      curToDate: to,
      curFromMinTime: this.calculateMinTime(from),
    });

  }



  getDialogButtons() {
    return {
      left: [
        {
          id: 'cancel',
          text: this.dialogLabels.get('cancel'),
          action: this.props.actions.hide,
        },
      ],
      right: [
        {
          id: 'submit',
          text: this.labels.get('footer.autoSchedule'),
          bsStyle: 'primary',
          loading: this.props.sData.get('loading'),
          action: this.props.handleSubmit(this.onSubmit),
          disabled:
            this.props.sData.get('loading') ||
            this.props.sData.get('hasError') ||
            (this.props.sData.get('showIgnoreValidationCheckbox') &&
              !this.props.sData.get('isIgnoreValidationWarnings')),
        },
      ],
    };
  }

  /**
   * @param {moment.Moment} date
   */
  calculateMinTime = (date) => {
    // Always return start of day to allow past times.
    // The minDateTimeValidate function will enforce the actual minimum datetime.
    return moment(date).startOf('day');
    
    // Deprecated. We now can schedule in the past.
    // let isToday = moment(date).isSame(moment(), 'day');
    // if (isToday) {
    //   let nowAddOneMinute = moment(new Date()).add({ minutes: 1 });
    //   return nowAddOneMinute;
    // }
    
  };


  /**
   * @param {moment.Moment} fromDate
   */
  onFromDateChange = (fromDate) => {
    
    // This should remain null for ui to show "Now" in the datepicker.
    let fixedFromDate = this.handleDateChangeCalculation(fromDate, 'fromHorizonDate');
    
    
    // Create variable that will hold the real "from" date.
    let realFromDate = fixedFromDate;
    if (!fixedFromDate) {
      realFromDate = moment().utcOffset(0, true).add(1, 'minutes');
    }
    

    ////////////////////////////////////////////////////////////////////////////
    // Dynamically calculate the min and max dates for the "to" date picker 
    // based on the "from" datepicker selection.
    //
    // The "to" datepicker is restricted to whatever the 
    // "from" datepicker is set to + Org specific scheduling period setting.
    // 

    let dynamicMinDate = moment(realFromDate);

    let dynamicMaxDate = moment(realFromDate).add(this.schedulingPeriodMonths, 'months');
    if (dynamicMaxDate.isAfter(this.maxDate)) {
      dynamicMaxDate = this.maxDate;
    }


    this.setState({
      curFromDate: fixedFromDate, // should be null to display "Now" in the datepicker.
      curFromMinTime: this.calculateMinTime(fromDate),

      dynamicMaxDate: dynamicMaxDate,
      dynamicMinDate: dynamicMinDate

    });


    // If the to date is now invalid (after the new max), adjust it.
    if (this.state.curToDate) {

      if (this.state.curToDate.isAfter(dynamicMaxDate)) {
        this.setState({
          curToDate: dynamicMaxDate
        });
        this.props.change('toHorizonDate', dynamicMaxDate.clone());
      }

      if (this.state.curToDate.isBefore(dynamicMinDate)) {
        this.setState({
          curToDate: dynamicMinDate
        });
        this.props.change('toHorizonDate', dynamicMinDate.clone());
      }

    }


  };


  /**
   * @param {moment.Moment} date
   */
  onToDateChange = (date) => {
    this.setState({
      curToDate: this.handleDateChangeCalculation(date, 'toHorizonDate'),
      curToMinTime: this.calculateMinTime(date),
    });
  };


  /**
   * @param {moment.Moment} date
   * @param {string} datePickerId
   */
  handleDateChangeCalculation = (date, datePickerId) => {
    
    if (!date) {
      return date;
    }
    
    
    if (!date.isUtcOffset()) {
      date.utcOffset(0, true);
    }
    
    // Force the "from" date to be not in the past. Deprecated.
    // const now = moment().utcOffset(0, true);
    // if (date?.isBefore(now)) {
    //   // manually change redux-form value because calculation takes place after datepicker onChange.
    //   this.props.change(datePickerId, moment().utcOffset(0, true).add(1, 'minutes'));
    //   return moment().add(1, 'minutes');
    // }

    return date;
  };

  /**
   * @param {moment.Moment} value
   * @param {{fromHorizonDate: moment.Moment, toHorizonDate: moment.Moment}} allValues
   */
  minDateTimeValidate = (value, allValues) => {

    // Deprecated. We now can schedule in the past.
    // let minDateTime = moment().utcOffset(0, true).add(1, 'minutes');

    let minDateTime = moment(this.minDate).utcOffset(0, true).add(1, 'minutes');
    return Validation.minDateTime(minDateTime)(value?.utcOffset(0, true), allValues);
  };

  /**
   * @param {moment.Moment} value
   * @param {{fromHorizonDate: moment.Moment, toHorizonDate: moment.Moment}} allValues
   */
  maxDateTimeValidate = (value, allValues) => {

    // let maxDateTime = this.maxDate.endOf('day');

    // Since we are now allowing scheduling in the past, 
    // the max date is now dynamic based on the "from" date + Org specific scheduling period.
    let maxDateTime = this.state.dynamicMaxDate.endOf('day');

    return Validation.maxDateTime(maxDateTime)(value, allValues);
  };

  /**
   * @param {moment.Moment} value
   * @param {{fromHorizonDate: moment.Moment, toHorizonDate: moment.Moment}} allValues
   */
  toDateValidate = (value, allValues) => {
    if (!value || !value.isValid()) {
      //do not care if value exists or has a proper format
      return undefined;
    }
    let fromDate =
      allValues && allValues.fromHorizonDate
        ? allValues.fromHorizonDate
        : moment().utcOffset(0, true);

    let isFromDateValueValid =
      fromDate && fromDate.isValid() && fromDate.isBefore(this.maxDate, 'minute');

    if (isFromDateValueValid && value.isBefore(fromDate)) {
      return this.labels.get('validation.laterThanFromDate');
    }

    return undefined;
  };


  /**
   * @param {{fromHorizonDate: moment.Moment, toHorizonDate: moment.Moment, footerValidationCheckbox: boolean}} data
   * 
   */
  onSubmit = (data) => {

    if (data) {

      let newData = {
        tasksAssignmentsIds: this.props.sData.get('itemsToEdit'),
        fromTime: DateTimeHelper.ConvertFromDate(data.fromHorizonDate),
        toTime: DateTimeHelper.ConvertFromDate(data.toHorizonDate),
        ignoreValidationWarnings:
          this.props.sData.get('showIgnoreValidationCheckbox') && data.footerValidationCheckbox
            ? data.footerValidationCheckbox
            : false,
      };

      this.props.actions.submit(newData, this.props.sSchedulerEngineInstance);
      
      this.storeDates({ 
        from: data.fromHorizonDate,
        to: data.toHorizonDate
      });
      
    }
  };

  onHide = () => {
    this.props.actions.hide();
  };

  onEntered = () => { };

  getFooterValidationCheckBox = () => {
    return this.props.sData.get('showIgnoreValidationCheckbox')
      ? { label: this.dialogLabels.get('ignorewarnings'), onChange: this.onFooterValidationChange }
      : null;
  };

  /** 
   * @param {boolean} value
   */
  onFooterValidationChange = (value) => {
    this.props.actions.toggleIgnoreValidationWarningsCheckbox(value);
  };

  render() {
    return (
      <Dialog
        id="auto-scheduler-dialog"
        className="auto-scheduler-dialog"
        titleText={this.labels.get('header.title')}
        show={this.props.sData.get('show')}
        onEntered={this.onEntered}
        onHide={this.onHide}
        sData={this.props.sData}
        // validationArea  = {this.getValidationArea()}
        footerValidationCheckbox={this.getFooterValidationCheckBox()}
        footerInformationIcon={dialogHelper.getFooterInformationIcon(
          this.props.sData,
          this.dialogLabels
        )}
        footerButtons={this.getDialogButtons()}
        onEnterKeyPress={this.props.handleSubmit(this.onSubmit)}
      >
        <span className="title">{this.labels.get('horizon.title')}</span>
        <InputSection
          label={this.labels.get('horizon.from') + '*'}
          htmlFor="fromHorizonDate"
          className="inline left-side"
        >
          <DatePicker
            id="fromHorizonDate"
            name="fromHorizonDate"
            showTimeSelect
            dateFormat={DateTimeHelper.getDateTimeFormat()}
            popperPlacement="auto"
            timeIntervals={10}
            isClearable={true}
            placeholderText={this.labels.get('horizon.from.placeholder')}
            onChangeCallback={this.onFromDateChange}
            selected={this.state.curFromDate}
            minDate={this.minDate}
            maxDate={this.maxDate}
            minTime={this.state.curFromMinTime}
            maxTime={this.state.curMaxTime}
            fixedHeight
            preventOpenOnFocus={true}
            validate={[Validation.date, this.minDateTimeValidate, this.maxDateTimeValidate]}
          />
        </InputSection>
        <InputSection
          label={this.labels.get('horizon.to') + '*'}
          htmlFor="toHorizonDate"
          className="inline right-side"
        >
          <DatePicker
            id="toHorizonDate"
            name="toHorizonDate"
            showTimeSelect
            dateFormat={DateTimeHelper.getDateTimeFormat()}
            popperPlacement="auto"
            initialDate={this.toInitialDate}
            timeIntervals={10}
            isClearable={true}
            onChangeCallback={this.onToDateChange}
            selected={this.state.curToDate}
            minDate={this.state.dynamicMinDate}
            maxDate={this.state.dynamicMaxDate}
            minTime={this.state.curToMinTime}
            maxTime={this.state.curMaxTime}
            fixedHeight
            preventOpenOnFocus={true}
            validate={[
              Validation.required,
              Validation.date,
              this.toDateValidate,
              this.minDateTimeValidate,
              this.maxDateTimeValidate,
            ]}
          />
        </InputSection>
      </Dialog>
    );
  }
}


AutoSchedulerDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
  sSchedulerEngineInstance: PropTypes.object.isRequired,
};


const ReduxFormEnhanced = reduxForm({
  form: 'autoSchedulerDialog',
  onChange: (values, dispatch, props, previousValues) => {
    dialogHelper.onFormChangeHandler(values, props, previousValues);
  },
  // @ts-ignore
})(AutoSchedulerDialog);

export default ReduxFormEnhanced;

